import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router';
import { useFromPathname } from '@/lib/navigation-context';

export function useUrlSearch(paramKey = 'q', baseRoute?: string) {
	const [searchParams, setSearchParams] = useSearchParams();
	const { pathname } = useLocation();
	const fromPathname = useFromPathname();
	const family = baseRoute ?? pathname;
	const storageKey = `url-search:${family}`;

	const urlValue = searchParams.get(paramKey) ?? '';

	const getInitialValue = () => {
		if (urlValue) return urlValue;
		if (!fromPathname.startsWith(family)) {
			sessionStorage.removeItem(storageKey);
			return '';
		}
		return sessionStorage.getItem(storageKey) ?? '';
	};

	const initialValue = getInitialValue();

	const [searchQuery, setSearchQuery] = useState(initialValue);
	const [debouncedSearch, setDebouncedSearch] = useState(initialValue);

	useEffect(() => {
		if (!urlValue && initialValue) {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set(paramKey, initialValue);
					return next;
				},
				{ replace: true }
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchQuery);

			if (searchQuery) sessionStorage.setItem(storageKey, searchQuery);
			else sessionStorage.removeItem(storageKey);

			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (searchQuery) next.set(paramKey, searchQuery);
					else next.delete(paramKey);
					return next;
				},
				{ replace: true }
			);
		}, 300);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery, paramKey, storageKey]);

	return { searchQuery, setSearchQuery, debouncedSearch };
}

export function useUrlFilters(paramKeys: readonly string[], baseRoute: string) {
	const [searchParams, setSearchParams] = useSearchParams();
	const { pathname } = useLocation();
	const fromPathname = useFromPathname();
	const family = baseRoute ?? pathname;
	const storageKey = `url-filters:${family}`;

	const readStorage = (): Record<string, string> => {
		try {
			return JSON.parse(sessionStorage.getItem(storageKey) ?? '{}');
		} catch {
			return {};
		}
	};

	const writeStorage = (data: Record<string, string>) => {
		if (Object.keys(data).length > 0) sessionStorage.setItem(storageKey, JSON.stringify(data));
		else sessionStorage.removeItem(storageKey);
	};

	useEffect(() => {
		const withinFamily = fromPathname.startsWith(family);
		if (!withinFamily) {
			sessionStorage.removeItem(storageKey);
			return;
		}
		const stored = readStorage();
		const toRestore = paramKeys.filter((k) => stored[k] && !searchParams.get(k));
		if (toRestore.length > 0) {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					toRestore.forEach((k) => next.set(k, stored[k]));
					return next;
				},
				{ replace: true }
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filters = useMemo(() => {
		const result: Record<string, string | undefined> = {};
		paramKeys.forEach((k) => {
			result[k] = searchParams.get(k) ?? undefined;
		});
		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	const setFilter = (key: string, value: string | undefined) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (value) next.set(key, value);
				else next.delete(key);
				return next;
			},
			{ replace: true }
		);
		const stored = readStorage();
		if (value) stored[key] = value;
		else delete stored[key];
		writeStorage(stored);
	};

	return { filters, setFilter };
}
